import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchVaccineApprovalsThunk } from '@spike/vaccine-approvals-action';
import { VaccineApproval } from 'model/VaccineApproval';
import { useApiClientWrapper } from './useApiClientWrapper';

interface UsePetPendingVaccineApprovalsResult {
    pendingApprovals: VaccineApproval[];
    loading: boolean;
    refresh: () => Promise<void>;
    removePending: (approvalId: number) => void;
}

export const usePetPendingVaccineApprovals = (
    petId: number | undefined,
    petName?: string
): UsePetPendingVaccineApprovalsResult => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const apiClientWrapperRef = useRef(apiClientWrapper);
    apiClientWrapperRef.current = apiClientWrapper;

    const [pendingApprovals, setPendingApprovals] = useState<VaccineApproval[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchPending = useCallback(async () => {
        if (!petId) {
            setPendingApprovals([]);
            return;
        }

        setLoading(true);
        try {
            const page = await (dispatch as any)(
                fetchVaccineApprovalsThunk(apiClientWrapperRef.current, {
                    status: 'pending',
                    search: petName?.trim() || undefined,
                    page: 1,
                    size: 100
                })
            );
            const requests: VaccineApproval[] = page?.requests ?? [];
            setPendingApprovals(requests.filter(request => request.pet.id === petId));
        } catch {
            setPendingApprovals([]);
        } finally {
            setLoading(false);
        }
    }, [dispatch, petId, petName]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!petId) {
                setPendingApprovals([]);
                return;
            }

            setLoading(true);
            try {
                const page = await (dispatch as any)(
                    fetchVaccineApprovalsThunk(apiClientWrapperRef.current, {
                        status: 'pending',
                        search: petName?.trim() || undefined,
                        page: 1,
                        size: 100
                    })
                );
                if (cancelled) return;
                const requests: VaccineApproval[] = page?.requests ?? [];
                setPendingApprovals(requests.filter(request => request.pet.id === petId));
            } catch {
                if (!cancelled) {
                    setPendingApprovals([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void run();

        return () => {
            cancelled = true;
        };
    }, [dispatch, petId, petName]);

    const removePending = useCallback((approvalId: number) => {
        setPendingApprovals(prev => prev.filter(approval => approval.id !== approvalId));
    }, []);

    return {
        pendingApprovals,
        loading,
        refresh: fetchPending,
        removePending
    };
};

export default usePetPendingVaccineApprovals;
